import React, { useRef, useState, useCallback, useMemo } from 'react';
import { Box, Paper, IconButton, Tooltip, Divider } from '@mui/material';
import { ZoomIn, ZoomOut, Refresh, PanTool, Cached as RetakeIcon } from '@mui/icons-material';
import { PanelHeader } from './PanelHeader';

const ZOOM_CONFIG = {
  MIN: 0.5,
  MAX: 5,
  STEP: 1.2,
} as const;

interface Transform {
  scale: number;
  x: number;
  y: number;
}

interface PanState {
  startX: number;
  startY: number;
  tx: number;
  ty: number;
}

interface InteractiveImageProps {
  title: string;
  imageUrl: string;
  showRetake?: boolean;
  onRetake?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const InteractiveImage: React.FC<InteractiveImageProps> = ({
  title,
  imageUrl,
  showRetake = false,
  onRetake,
}) => {
  const [transform, setTransform] = useState<Transform>({ scale: 1, x: 0, y: 0 });
  const [isPanMode, setIsPanMode] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const panStartRef = useRef<PanState | null>(null);

  const handleRetakeClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onRetake) {
        onRetake(event);
      }
      if (event.target) {
        event.target.value = '';
      }
    },
    [onRetake]
  );

  const adjustZoom = useCallback((direction: 'in' | 'out') => {
    setTransform((prev) => {
      const newScale =
        direction === 'in' ? prev.scale * ZOOM_CONFIG.STEP : prev.scale / ZOOM_CONFIG.STEP;

      return {
        ...prev,
        scale: Math.max(ZOOM_CONFIG.MIN, Math.min(newScale, ZOOM_CONFIG.MAX)),
      };
    });
  }, []);

  const resetView = useCallback(() => {
    setTransform({ scale: 1, x: 0, y: 0 });
  }, []);

  const togglePanMode = useCallback(() => {
    setIsPanMode((prev) => !prev);
  }, []);

  const handlePanStart = useCallback(
    (e: React.MouseEvent) => {
      if (!isPanMode) return;

      e.preventDefault();
      panStartRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        tx: transform.x,
        ty: transform.y,
      };
    },
    [isPanMode, transform.x, transform.y]
  );

  const handlePanMove = useCallback((e: React.MouseEvent) => {
    const panStart = panStartRef.current;
    if (!panStart) return;

    e.preventDefault();

    const dx = e.clientX - panStart.startX;
    const dy = e.clientY - panStart.startY;

    setTransform((prev) => ({
      ...prev,
      x: panStart.tx + dx,
      y: panStart.ty + dy,
    }));
  }, []);

  const handlePanEnd = useCallback(() => {
    panStartRef.current = null;
  }, []);

  const cursorStyle = useMemo(
    () => (isPanMode ? (panStartRef.current ? 'grabbing' : 'grab') : 'default'),
    [isPanMode]
  );

  const imageTransformStyle = useMemo(
    () => ({
      width: '100%',
      height: '100%',
      objectFit: 'contain' as const,
      display: 'block',
      transition: panStartRef.current ? 'none' : 'transform 0.1s linear',
      transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
      transformOrigin: 'center center',
      userSelect: 'none' as const,
      pointerEvents: 'none' as const,
    }),
    [transform.x, transform.y, transform.scale]
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/jpeg,image/png,image/jpg"
        style={{ display: 'none' }}
        aria-label="Upload image"
      />

      <PanelHeader title={title}>
        {showRetake && onRetake && (
          <Tooltip title="Retake / Change Image">
            <IconButton onClick={handleRetakeClick} size="small" color="primary" aria-label="retake image">
              <RetakeIcon />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Zoom Out">
          <IconButton
            onClick={() => adjustZoom('out')}
            size="small"
            disabled={transform.scale <= ZOOM_CONFIG.MIN}
            aria-label="zoom out"
          >
            <ZoomOut />
          </IconButton>
        </Tooltip>

        <Tooltip title="Reset View">
          <IconButton onClick={resetView} size="small" aria-label="reset view">
            <Refresh />
          </IconButton>
        </Tooltip>

        <Tooltip title="Zoom In">
          <IconButton
            onClick={() => adjustZoom('in')}
            size="small"
            disabled={transform.scale >= ZOOM_CONFIG.MAX}
            aria-label="zoom in"
          >
            <ZoomIn />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

        <Tooltip title={isPanMode ? 'Disable Pan Mode' : 'Enable Pan Mode'}>
          <IconButton
            onClick={togglePanMode}
            color={isPanMode ? 'primary' : 'default'}
            size="small"
            aria-label="toggle pan mode"
            aria-pressed={isPanMode}
          >
            <PanTool />
          </IconButton>
        </Tooltip>
      </PanelHeader>

      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          cursor: cursorStyle,
          backgroundColor: 'action.hover',
          position: 'relative',
        }}
        onMouseDown={handlePanStart}
        onMouseMove={handlePanMove}
        onMouseUp={handlePanEnd}
        onMouseLeave={handlePanEnd}
      >
        <Box component="img" src={imageUrl} alt={title} sx={imageTransformStyle} draggable={false} />
      </Box>
    </Paper>
  );
};